import clsx from 'clsx';
import { type ReactNode, useEffect, useEffectEvent, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BiCheck, BiChevronRight } from 'react-icons/bi';
import { FiCheck } from 'react-icons/fi';

import { Card, StatusPill } from '@/components';

import { type Choice, type FeatureOption } from './OnboardingWizard.constants';
import s from './OnboardingWizard.module.scss';

/** Toggle-able pill used by the service types and job roles steps. */
export function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className={`${s.chip} ${active ? s.chipSelected : ''}`} onClick={onClick}>
      {active && <FiCheck size={13} />}
      {children}
    </button>
  );
}

/** Single-select cards used for intent / role / job-path steps. */
export function ChoiceCards<T extends string>({
  items,
  selectedId,
  onSelect,
  footer,
}: {
  items: Choice<T>[];
  selectedId: T | null;
  onSelect: (id: T) => void;
  footer?: ReactNode;
}) {
  return (
    <>
      <div className={s.choiceGrid}>
        {items.map(({ id, title, desc, Icon, badge }) => (
          <Card
            key={id}
            padding="none"
            className={clsx(s.choiceTile, selectedId === id && s.choiceTileSelected)}
            onClick={() => onSelect(id)}
            ariaLabel={title}
          >
            <span className={s.choiceIcon} aria-hidden="true">
              <Icon />
            </span>
            <span className={s.choiceBody}>
              <span className={s.choiceTitle}>{title}</span>
              <span className={s.choiceDesc}>{desc}</span>
            </span>
            {(badge || selectedId === id) && (
              <span className={s.choiceCorner}>
                {badge && <StatusPill label={badge} tone="info" />}
                {selectedId === id && <FiCheck className={s.choiceCheck} aria-hidden="true" />}
              </span>
            )}
          </Card>
        ))}
      </div>
      {footer}
    </>
  );
}

/** Multi-select feature/role grid. */
export function FeatureGrid({
  items,
  selected,
  onToggle,
}: {
  items: FeatureOption[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className={s.featureGrid}>
      {items.map(({ id, label, Icon }) => {
        const on = selected.includes(id);
        return (
          <button
            type="button"
            key={id}
            className={`${s.featureCard} ${on ? s.featureCardOn : ''}`}
            onClick={() => onToggle(id)}
          >
            <span className={s.featureIcon}>
              <Icon size={20} />
            </span>
            <span className={s.featureLabel}>{label}</span>
            <span className={s.featureCheck}>{on && <FiCheck size={14} />}</span>
          </button>
        );
      })}
    </div>
  );
}

/** One line of the finish setup, in two tenses: while it runs, and once it has. */
export interface SetupTask {
  doing: string;
  done: string;
}

/** How long each line of the setup takes to tick off. */
const SETUP_STEP_MS = 700;
/** How long the finished list stays up before the success screen takes over. */
const SETUP_HOLD_MS = 800;

/**
 * The beat between the last step and the success screen: the whole viewport, each part of
 * the setup ticking off in turn so the work just done is visible, then "All set!" and it
 * hands over by itself. The first task is taken as already done — the account exists.
 * Under reduced motion every line is ticked at once and there is no hold: the list is the
 * information, the pacing only decoration, and the success screen repeats it.
 */
export function FinishSetup({ tasks, onDone }: { tasks: SetupTask[]; onDone: () => void }) {
  const [reducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const [ticked, setTicked] = useState(reducedMotion ? tasks.length : 1);
  const done = ticked >= tasks.length;
  const finish = useEffectEvent(onDone);

  useEffect(() => {
    const t = done
      ? window.setTimeout(() => finish(), reducedMotion ? 0 : SETUP_HOLD_MS)
      : window.setTimeout(() => setTicked((n) => n + 1), SETUP_STEP_MS);
    return () => window.clearTimeout(t);
  }, [ticked, done, reducedMotion]);

  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => rootRef.current?.focus(), []);

  return createPortal(
    <div
      ref={rootRef}
      className={s.setupTakeover}
      role="dialog"
      aria-modal="true"
      aria-labelledby="onb-setup-title"
      tabIndex={-1}
    >
      <div className={s.setupInner}>
        <span className={clsx(s.setupPulse, done && s.setupPulseDone)} aria-hidden="true">
          {done && <BiCheck />}
        </span>
        <h2 className={s.finishTitle} id="onb-setup-title">
          {done ? 'All set!' : 'Setting things up…'}
        </h2>
        <ul className={s.setupList} aria-live="polite">
          {tasks.map((task, i) => {
            const state = i < ticked ? 'done' : i === ticked ? 'now' : 'later';
            return (
              <li
                key={task.doing}
                className={clsx(
                  state === 'done' && s.setupDone,
                  state === 'now' && s.setupNow,
                  state === 'later' && s.setupLater,
                )}
              >
                <span className={s.setupDot} aria-hidden="true">
                  {state === 'done' && <BiCheck />}
                </span>
                {state === 'done' ? task.done : task.doing}
                {state === 'now' && <span className={s.srOnly}> — in progress</span>}
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The success screen's first things to do: upright tiles three to a row — icon, the words
 * at the tile's full width, an arrow in the corner — lying flat one to a row on a phone.
 */
export function NextStepTiles<T extends string>({
  items,
  onPick,
}: {
  items: Choice<T>[];
  onPick: (id: T) => void;
}) {
  return (
    <div className={s.nextGrid}>
      {items.map(({ id, title, desc, Icon }) => (
        <Card
          key={id}
          padding="none"
          className={s.nextTile}
          onClick={() => onPick(id)}
          ariaLabel={title}
        >
          <span className={s.nextIcon} aria-hidden="true">
            <Icon />
          </span>
          <span className={s.nextText}>
            <span className={s.nextName}>{title}</span>
            <span className={s.nextHint}>{desc}</span>
          </span>
          <BiChevronRight className={s.nextArrow} aria-hidden="true" />
        </Card>
      ))}
    </div>
  );
}
